import Axios from "./caller.service";

// =====================================================================
// SERVICE : vue ADMIN transverse des demandes (superAdmin / supportTeam)
// =====================================================================
// Appels vers les endpoints admin qui listent TOUTES les demandes
// (calibration / SAV / support), avec filtres + pagination cote serveur,
// et re-telechargement des PDF encore disponibles.
// =====================================================================

/**
 * Recupere la liste paginee/filtree de toutes les demandes.
 * @param {object} params
 * @param {number} params.page
 * @param {number} params.pageSize
 * @param {string} [params.type]       "calibration" | "sav" | "support"
 * @param {string} [params.search]
 * @param {string} [params.dateFrom]   "YYYY-MM-DD"
 * @param {string} [params.dateTo]     "YYYY-MM-DD"
 * @param {string} [params.pdfStatus]  "available" | "archived"
 * @param {number} [params.userId]
 * @param {AbortSignal} [signal]
 * @returns {Promise<{ data: Array, pagination: object }>}
 */
export const getAllAdminRequests = async (params = {}, signal) => {
  const query = {};
  if (params.page) query.page = params.page;
  if (params.pageSize) query.pageSize = params.pageSize;
  if (params.type) query.type = params.type;
  if (params.search) query.search = params.search;
  if (params.dateFrom) query.dateFrom = params.dateFrom;
  if (params.dateTo) query.dateTo = params.dateTo;
  if (params.pdfStatus) query.pdfStatus = params.pdfStatus;
  if (params.userId) query.userId = params.userId;

  const res = await Axios.get("/adminRequests/all", { params: query, signal });
  return {
    data: res?.data?.data || [],
    pagination: res?.data?.pagination || { page: 1, pageSize: 25, total: 0, totalPages: 1 },
  };
};

/**
 * Telecharge le PDF d'une demande (vue admin) et declenche le download.
 * @param {string} type       "calibration" | "sav" | "support"
 * @param {number} requestId
 * @param {string} filename
 */
export const downloadAdminRequestPdf = async (type, requestId, filename) => {
  const response = await Axios.get(
    `/adminRequests/download/${type}/${requestId}`,
    { responseType: "blob" }
  );

  const blob = new Blob([response.data], { type: "application/pdf" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename || `demande_${requestId}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * Recupere les agregats statistiques des demandes pour une periode.
 * @param {string} period "7d" | "30d" | "12m" | "all"
 * @param {AbortSignal} [signal]
 * @returns {Promise<object>} objet stats (kpi, timeline, topCabines, ...).
 */
export const getAdminRequestsStats = async (period = "30d", signal) => {
  const res = await Axios.get("/adminRequests/stats", {
    params: { period },
    signal,
  });
  return res?.data?.data || null;
};

/**
 * Recupere le detail complet d'une demande (toutes les metadonnees).
 * @param {string} type       "calibration" | "sav" | "support"
 * @param {number} requestId
 * @param {AbortSignal} [signal]
 * @returns {Promise<object|null>}
 */
export const getAdminRequestDetail = async (type, requestId, signal) => {
  const res = await Axios.get(`/adminRequests/detail/${type}/${requestId}`, {
    signal,
  });
  return res?.data?.data || null;
};
